// @flow
import * as React from 'react';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogContent,
  DialogTitle, Divider,
  IconButton,
  Stack, Typography
} from '@mui/material';
import { ModalCloseButton } from '@coverright/ui/shared';
import { Pen, X } from '@phosphor-icons/react';
import {
  useClientGroupByClientIdQuery, useDeleteClientsFromAnyGroupMutation,
  useMergeClientsInOneGroupMutation
} from '../../../../../shared';
import { getUserName } from '@coverright/utils';
import { LoadingButton } from '@mui/lab';
import { useClient } from '../../../hooks/client-context';
import _ from 'lodash';
import { ClientSelector } from '../../../../../features';
import { ClientGroupItemOutput } from '@coverright/data-access/types/enrollment';

type Props = {
  onSubmit: () => void
};
export const ClientGroupsForm = (props: Props) => {
  const client = useClient();
  const {data: groupData, refetch} = useClientGroupByClientIdQuery({
    variables: {clientId: client?.id},
    skip: !client?.id
  });

  const existingInGroup = useMemo(() => groupData?.clientGroupByClientId?.clients || [], [groupData?.clientGroupByClientId?.clients]);

  const [clients, setClients] = useState<ClientGroupItemOutput[]>([]);

  useEffect(() => {
    if (existingInGroup.length) {
      setClients(existingInGroup);
    }
  }, [existingInGroup]);

  const [save, {loading: saveLoading}] = useMergeClientsInOneGroupMutation();
  const [deleteClients] = useDeleteClientsFromAnyGroupMutation();


  const submit = async () => {
    const current = existingInGroup.map(c => c.clientId)
    const clientsToMerge = _.uniq([...clients.map(c => c.clientId), client.id]);
    const clientsToDelete = _.differenceBy(current, clientsToMerge);

    await save({variables: {clientIds: clientsToMerge}});
    if (clientsToDelete.length) {
      await deleteClients({variables: {clientIds: clientsToDelete}})
    }

    refetch();
    props.onSubmit();
  };

  const handleDeleteClick = async (client: ClientGroupItemOutput) => {
    setClients(clients.filter(c => c.clientId !== client.clientId))
  }

  return (
    <Stack gap={1}>
      <ClientSelector sx={{mb: 1}} onChange={value => setClients(prev => [...prev, value as any])} />

      {clients?.map((client, i, array) => (
        <React.Fragment key={client.clientId}>
          <Stack gap={2} direction={'row'} alignItems={'center'} width={1}>
            <Box flex={1}>
              <Typography variant={'body2'}>{getUserName(client)}</Typography>
              <Typography variant={'caption'} color={'text.secondary'}>{client.email}</Typography>
            </Box>
            <IconButton size={'small'} onClick={r => handleDeleteClick(client)}>
              <X size={16} color={'#DE3124'} />
            </IconButton>
          </Stack>
          {i < array.length - 1 && <Divider />}
        </React.Fragment>
      ))}

      <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
        <Button onClick={() => props.onSubmit()} variant={'outlined'}>
          Cancel
        </Button>
        <LoadingButton
          loading={saveLoading}
          onClick={submit}
          variant={'contained'}
        >
          Save
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

export const ClientGroupsEditButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const [open, setOpen] = useState(false);

  return <>
    <Dialog fullWidth maxWidth={'xs'} open={open} onClose={() => setOpen(false)}>
      <DialogTitle variant={'h3'}>
        Client groups
        <ModalCloseButton onClick={() => setOpen(false)} sx={{position: 'absolute', top: 4, right: 8}} />
      </DialogTitle>
      <DialogContent>
        <ClientGroupsForm onSubmit={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
    <IconButton ref={ref} size={'small'} {...props} onClick={e => {
      props.onClick && props.onClick(e)
      setOpen(true)
    }}>
      <Pen color="#a6a6a6" />
    </IconButton>
  </>
})
